import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ExplicitContent from "./pages/ExplicitContent";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Stories from "./pages/Stories";
import Article from "./pages/Article";
import Merch from "./pages/Merch";
import GanglandCards from "./pages/GanglandCards";
import SubmitTip from "./pages/SubmitTip";
import Voicemail from "./pages/Voicemail";
import TipWall from "./pages/TipWall";
import About from "./pages/About";
import PressKit from "./pages/PressKit";
import CrimeMap from "./pages/CrimeMap";
import NotFound from "./pages/NotFound";
import BannedPage from "./pages/BannedPage";
import Honeypot from "./pages/Honeypot";
import Experiences from "./pages/Experiences";
import Login from "./pages/Login";
import AuthVerify from "./pages/AuthVerify";
import AuthGoogleCallback from "./pages/AuthGoogleCallback";
import Subscribe from "./pages/Subscribe";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Account from "./pages/Account";
import Founders from "./pages/Founders";
import OpSec from "./pages/OpSec";
import ScrollToTop from "./components/ScrollToTop";
import UtmCapture from "./components/UtmCapture";
import UsageHeartbeat from "./components/UsageHeartbeat";
import MaintenanceOverlay from "./components/MaintenanceOverlay";
import { Toaster } from "sonner";
import PullToRefreshIndicator from "./components/sections/PullToRefreshIndicator";
import { usePullToRefresh } from "./hooks/usePullToRefresh";
import { AuthProvider } from "./context/AuthContext";
import SectionLockOverlay from "./components/SectionLockOverlay";
import FreeAccessPopup from "./components/FreeAccessPopup";
import BreakingNewsIntro from "./components/BreakingNewsIntro";

const API = process.env.REACT_APP_BACKEND_URL;

// Obscure admin route (option-b style). Override via env without a code push.
const ADMIN_SLUG = (process.env.REACT_APP_ADMIN_SLUG || "q7-r3d-vault-9k2").replace(/^\/+/, "");
export const ADMIN_LOGIN_PATH = `/${ADMIN_SLUG}`;
export const ADMIN_DASHBOARD_PATH = `/${ADMIN_SLUG}/dashboard`;

function App() {
  // Suppress on admin routes (no need on a CRUD dashboard)
  const isAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith(`/${ADMIN_SLUG}`);
  const { pull, refreshing } = usePullToRefresh(
    () => new Promise((r) => { window.location.reload(); setTimeout(r, 300); }),
    { enabled: !isAdmin },
  );

  // Site-wide ban check. Runs on every fresh app load; if the caller's IP
  // has been blocklisted by an admin the entire router is replaced with
  // BannedPage. We deliberately do NOT run this on admin routes so a
  // banned IP that happens to also know the admin slug still can't get in
  // via a workaround - the /api/admin/* endpoints are protected by
  // the ban middleware anyway (login will 403 for them).
  const [ban, setBan] = useState({ checked: false, banned: false, data: null });
  useEffect(() => {
    if (isAdmin) { setBan({ checked: true, banned: false, data: null }); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/security/ban-status`, { cache: 'no-store' });
        if (!res.ok) throw new Error('bad response');
        const data = await res.json();
        if (cancelled) return;
        setBan({
          checked: true,
          banned: !!data.banned,
          data: data.banned ? data : null,
        });
      } catch {
        // If the check itself fails we let the user through - better to
        // fail-open than lock everyone out on a network blip.
        if (!cancelled) setBan({ checked: true, banned: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // While we haven't heard back yet, render nothing to avoid a flash of
  // the real site before the ban page snaps in for a blocked user.
  if (!ban.checked) {
    return <div className="App" data-testid="app-ban-check" />;
  }

  if (ban.banned) {
    return (
      <div className="App">
        <BannedPage />
      </div>
    );
  }

  return (
    <div className="App">
      <PullToRefreshIndicator pull={pull} refreshing={refreshing} />
      <Toaster theme="dark" position="top-center" richColors closeButton />
      <BrowserRouter>
        <ScrollToTop />
        <UtmCapture />
        <MaintenanceOverlay />
        <AuthProvider>
        <UsageHeartbeat />
        <SectionLockOverlay />
        <FreeAccessPopup />
        <BreakingNewsIntro />
        <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

/**
 * Global hash sniffer for the Emergent Google-auth return hop.
 * If the URL fragment contains `session_id=`, mount AuthGoogleCallback
 * FIRST - before any protected route renders - so the callback can swap
 * the session_id for our cookie and navigate away cleanly.
 *
 * Uses `useLocation().hash` (reactive) rather than `window.location.hash`
 * per the playbook, so the callback screen doesn't get stuck after we
 * clear the hash via history.replaceState.
 */
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthGoogleCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<Home />} />
            <Route path={ADMIN_LOGIN_PATH} element={<AdminLogin />} />
            <Route path={ADMIN_DASHBOARD_PATH} element={<AdminDashboard />} />
            <Route path="/explicit" element={<ExplicitContent />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories/:slug" element={<Article />} />
            <Route path="/merch" element={<Merch />} />
            <Route path="/ganglandcards" element={<GanglandCards />} />
            <Route path="/tip" element={<SubmitTip />} />
            <Route path="/voicemail" element={<Voicemail />} />
            <Route path="/the-rumour-file" element={<TipWall />} />
            <Route path="/wall" element={<Navigate to="/the-rumour-file" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/press" element={<PressKit />} />
            <Route path="/ganglandmap" element={<CrimeMap />} />
            <Route path="/map" element={<Navigate to="/ganglandmap" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/verify" element={<AuthVerify />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/account" element={<Account />} />
            <Route path="/founders" element={<Founders />} />
            <Route path="/opsec" element={<OpSec />} />
            {/* Honeypot tracking link - silently captures the visitor's
                fingerprint, emails admin, then displays their IP + "Best of
                luck." Not indexed. Distributed only to targeted individuals. */}
            <Route path="/private/:slug" element={<Honeypot />} />
            {/* 72-hour anonymous experience-reporting window */}
            <Route path="/experiences" element={<Experiences />} />
            {/* Honeypot - old admin paths now return 404 instead of revealing the new one */}
            <Route path="/admin" element={<NotFound />} />
            <Route path="/admin/*" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
  );
}

export default App;
